import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { throttle, useResizeObserver } from '@direct-frontend/stdlib';

type CarouselInfo = {
    isInitialized: boolean;
    slidesInViewport: number;
    measurements: Array<{
        offsetLeft: number;
        offsetWidth: number;
        offsetTop: number;
        offsetHeight: number;
    }>;
};

export type UseCarouselProps = {
    count: number;
    currentIndex: number;
    onIndexChange?: (index: number) => void;
    carouselRef: MutableRefObject<HTMLElement | null>;
    carouselTrackRef: MutableRefObject<HTMLElement | null>;
    vertical?: boolean;
};

export type UseCarouselController = {
    controls: {
        isPrevEnabled: boolean;
        isNextEnabled: boolean;
    };
    onTrackScroll: VoidFunction;
    onPrevSlide: VoidFunction;
    onNextSlide: VoidFunction;
    onIndexChange: (index: number, behavior?: ScrollBehavior) => void;
};

// Нужен чтобы на сужение в 1px не моргали стрелки и не менялся slidesinViewPort
const BUFFER_PX = 1;

function parseElementRowGap(element: HTMLElement): number {
    const computedStyles = getComputedStyle(element);
    const spacing = computedStyles.rowGap ?? computedStyles.gap;

    return parseFloat(spacing) || 0;
}

export function useCarousel(props: UseCarouselProps): UseCarouselController {
    const { count, currentIndex, carouselRef, carouselTrackRef, onIndexChange, vertical = false } = props;
    const isScrollingRef = useRef(false);
    const isInitializedRef = useRef(false);

    const [controls, setControls] = useState<{
        isPrevEnabled: boolean;
        isNextEnabled: boolean;
    }>({ isPrevEnabled: false, isNextEnabled: false });

    const onUpdateControls = useCallback(() => {
        if (carouselTrackRef.current) {
            if (vertical) {
                const scrollHeight = carouselTrackRef.current.scrollHeight;
                const offsetHeight = carouselTrackRef.current.offsetHeight;
                const scrollTop = carouselTrackRef.current.scrollTop;

                setControls({
                    isPrevEnabled: scrollTop > 0,
                    isNextEnabled: Math.round(scrollTop + offsetHeight) < scrollHeight,
                });
            } else {
                const scrollWidth = carouselTrackRef.current.scrollWidth;
                const offsetWidth = carouselTrackRef.current.offsetWidth;
                const scrollLeft = carouselTrackRef.current.scrollLeft;

                setControls({
                    isPrevEnabled: scrollLeft > 0,
                    isNextEnabled: Math.round(scrollLeft + offsetWidth) < scrollWidth,
                });
            }
        }
    }, [carouselTrackRef, vertical]);

    const [carouselInfo, setCarouselInfo] = useState<CarouselInfo>({
        isInitialized: false,
        slidesInViewport: 1,
        measurements: [],
    });

    const onResize = useCallback(() => {
        if (carouselTrackRef.current) {
            const measurements = (Array.from(carouselTrackRef.current.children) as HTMLElement[]).map((element) => {
                return {
                    offsetLeft: element.offsetLeft,
                    offsetWidth: element.offsetWidth,
                    offsetTop: element.offsetTop,
                    offsetHeight: element.offsetHeight,
                };
            });

            const spacing = parseElementRowGap(carouselTrackRef.current);
            let slidesInViewport: number;

            if (vertical) {
                const slideHeight = measurements[0]?.offsetHeight || 1;
                const containerHeight = carouselTrackRef.current.offsetHeight;

                slidesInViewport = Math.floor((containerHeight + BUFFER_PX + spacing) / (slideHeight + spacing)) || 1;
            } else {
                const slideWidth = measurements[0]?.offsetWidth || 1;
                const containerWidth = carouselTrackRef.current.offsetWidth;

                slidesInViewport = Math.floor((containerWidth + BUFFER_PX + spacing) / (slideWidth + spacing)) || 1;
            }

            onUpdateControls();
            setCarouselInfo({ isInitialized: true, slidesInViewport, measurements });
        }
    }, [carouselTrackRef, onUpdateControls, vertical]);

    const observer = useResizeObserver(onResize);

    useEffect(() => {
        if (observer && carouselRef.current) {
            observer.observe(carouselRef.current);
            onResize();
        }

        return () => observer?.disconnect();
    }, [carouselRef, count, observer, onResize]);

    // Сбрасываем флаг инициализации при изменении количества элементов
    useEffect(() => {
        isInitializedRef.current = false;
    }, [count]);

    const onScrollToIndex = useCallback(
        (index: number, behavior: ScrollBehavior = 'smooth') => {
            if (!carouselTrackRef.current) {
                return;
            }

            if (vertical) {
                const targetTop = carouselInfo.measurements[index]?.offsetTop ?? 0;
                const maxScrollTop = carouselTrackRef.current.scrollHeight - carouselTrackRef.current.clientHeight;
                const finalTop = targetTop > maxScrollTop ? maxScrollTop : targetTop;

                carouselTrackRef.current.scrollTo({ top: finalTop, behavior });
            } else {
                const targetLeft = carouselInfo.measurements[index]?.offsetLeft ?? 0;
                const maxScrollLeft = carouselTrackRef.current.scrollWidth - carouselTrackRef.current.clientWidth;
                const finalLeft = targetLeft > maxScrollLeft ? maxScrollLeft : targetLeft;

                carouselTrackRef.current.scrollTo({ left: finalLeft, behavior });
            }
        },
        [carouselInfo.measurements, carouselTrackRef, vertical]
    );

    const getScrollIndex = useCallback(() => {
        if (!carouselTrackRef.current) {
            return currentIndex;
        }

        if (vertical) {
            const scrollOffset = Math.round(carouselTrackRef.current.scrollTop);
            const containerHeight = carouselTrackRef.current.offsetHeight;

            // Находим элемент, который виден больше чем на 50%
            for (let index = 0; index < carouselInfo.measurements.length; index++) {
                const measurement = carouselInfo.measurements[index];
                const elementTop = measurement.offsetTop;
                const elementBottom = elementTop + measurement.offsetHeight;
                const viewportTop = scrollOffset;
                const viewportBottom = scrollOffset + containerHeight;

                const visibleTop = Math.max(elementTop, viewportTop);
                const visibleBottom = Math.min(elementBottom, viewportBottom);
                const visibleArea = Math.max(0, visibleBottom - visibleTop);
                const visibilityPercentage = visibleArea / measurement.offsetHeight;

                if (visibilityPercentage > 0.5) {
                    return index;
                }
            }

            return currentIndex;
        }

        const scrollOffset = Math.round(carouselTrackRef.current.scrollLeft);

        return carouselInfo.measurements.findIndex(
            (measurement) =>
                Math.round(measurement.offsetLeft) + Math.round(measurement.offsetWidth / 2) >= scrollOffset
        );
    }, [carouselInfo, carouselTrackRef, currentIndex, vertical]);

    /* eslint-disable react-hooks/exhaustive-deps -- Автоматический игнор в рамках DIRECT-312943 */
    const onTrackScrollEnd = useCallback(
        // eslint-disable-next-line react-hooks/use-memo, react-hooks/refs -- Автоматический игнор в рамках DIRECT-312943
        throttle(() => {
            onUpdateControls();

            const newIndex = getScrollIndex();

            if (currentIndex !== newIndex) {
                onIndexChange?.(newIndex);
            }

            isScrollingRef.current = false;
        }, 50),
        [currentIndex, getScrollIndex, onIndexChange]
    );
    /* eslint-enable react-hooks/exhaustive-deps */

    const onTrackScroll = useCallback(() => {
        isScrollingRef.current = true;
        onTrackScrollEnd();
    }, [onTrackScrollEnd]);

    const onChangeSlide = useCallback(
        (newIndex: number, behavior?: ScrollBehavior) => {
            onIndexChange?.(newIndex);

            if (!vertical || behavior) {
                onScrollToIndex(newIndex, behavior);
            }
        },
        [onIndexChange, onScrollToIndex, vertical]
    );

    const onPrevSlide = useCallback(() => {
        const offset = Math.max(1, Math.round(carouselInfo.slidesInViewport / 2));
        const newIndex = Math.max(0, currentIndex - offset);

        setControls({
            isPrevEnabled: newIndex > 0,
            isNextEnabled: true,
        });

        onChangeSlide(newIndex);
    }, [carouselInfo.slidesInViewport, currentIndex, onChangeSlide]);

    const onNextSlide = useCallback(() => {
        const offset = Math.max(1, Math.round(carouselInfo.slidesInViewport / 2));
        const newIndex = Math.min(count - 1, currentIndex + offset);

        setControls({
            isPrevEnabled: true,
            isNextEnabled: newIndex < count - 1,
        });

        onChangeSlide(newIndex);
    }, [currentIndex, carouselInfo.slidesInViewport, count, onChangeSlide]);

    useEffect(() => {
        if (carouselInfo.isInitialized && carouselInfo.measurements.length > 0) {
            if (!isInitializedRef.current) {
                isInitializedRef.current = true;

                // Только при первой инициализации скроллим к нужному индексу
                if (currentIndex !== 0) {
                    onScrollToIndex(currentIndex, 'instant');
                }
            }
        }
    }, [carouselInfo.isInitialized, carouselInfo.measurements.length, currentIndex, onScrollToIndex, vertical]);

    return {
        controls,
        onNextSlide,
        onPrevSlide,
        onTrackScroll,
        onIndexChange: onChangeSlide,
    };
}
